import { SimpleUser, type SimpleUserOptions } from "sip.js/lib/platform/web";
import { SessionState, UserAgent, type Invitation, type Session } from "sip.js/lib/api";
import type { SipCredentials } from "../api/agent";

export type PhoneState =
  | "offline"
  | "registering"
  | "idle"
  // An outbound INVITE we sent is still in flight: dialling, ringing at the
  // far end, or about to be refused. Distinct from "ringing", which means a
  // call is coming *in*. Without this the console had no way to know a manual
  // dial was underway at all — SimpleUser.call() resolves once the INVITE is
  // SENT, so the promise says nothing about whether anyone is ringing.
  | "calling"
  | "ringing"
  | "on-call";

export interface SoftphoneCallbacks {
  onState: (state: PhoneState) => void;
  onIncoming: (from: string) => void;
  onError: (message: string) => void;
  /**
   * One outbound call was refused — busy, declined, not in service. Separate
   * from `onError` on purpose: `onError` means the softphone itself is in
   * trouble (registration lost, transport dropped), which consumers rightly
   * collapse into one "can't reach the phone server" message. A single number
   * declining is nothing of the sort, and saying so tells an operator their
   * platform is down when one call didn't go through. The message is already
   * agent-readable — show it as-is.
   *
   * Optional: a consumer that only places calls it doesn't report on (the
   * connectivity self-test) has no use for it.
   */
  onCallFailed?: (message: string) => void;
  /**
   * The call is up and audio is arriving, but this device is not *playing* it —
   * the agent hears silence while the far end hears them fine.
   *
   * This exists because that failure was previously invisible. SIP.js drives
   * the remote `<audio>` element itself (session-manager.js: `srcObject = ...;
   * play().catch(e => this.logger.error(e))`), so a rejected play() — an iOS
   * autoplay block being the usual reason — ends its life inside SIP.js's
   * logger and never reaches the application. Nothing above it could tell a
   * silent phone from a working one, so the console showed a normal call.
   *
   * `resumePlayback()` is the recovery, and it MUST be called from a real user
   * gesture (a tap), which is the whole reason this is a callback the UI
   * reacts to rather than something the service can retry on its own.
   *
   * Optional: the connectivity self-test reads playbackBlocked() directly.
   */
  onAudioBlocked?: () => void;
}

/**
 * The two transfer failures an agent can actually act on: a number they can
 * retype, and a call that has already gone away. Everything else (a REFER the
 * far end rejects, a dead transport) is not actionable mid-call, so the UI
 * collapses it into one generic message and logs the detail.
 *
 * Exported so the console can compare identity rather than sniff message text.
 */
export const TRANSFER_INVALID_TARGET = "Enter a valid phone number or extension";
export const TRANSFER_NO_CALL = "That call already ended";

/** Same shape as TRANSFER_INVALID_TARGET, for call()'s target validation. */
export const CALL_INVALID_TARGET = "Enter a valid phone number or extension";
/** call() requires an existing registration; SimpleUser also refuses a
 * second concurrent OUTBOUND session. (It does NOT refuse a second inbound
 * one — see `refuseSecondCall` below.) */
export const CALL_NOT_CONNECTED = "Not connected";

/**
 * hold()/unhold() need a live, established session — a re-INVITE has nothing
 * to renegotiate otherwise. Same wording as TRANSFER_NO_CALL: from the
 * agent's side both mean "the thing you were about to act on is gone".
 */
export const HOLD_NO_CALL = "That call already ended";

/**
 * Surfaced via `onError` whenever Asterisk REJECTS a REGISTER (or a
 * previously-accepted registration lapses without a successful refresh) —
 * see the `onUnregistered` delegate handler below. Exported so the console
 * and its tests can compare identity rather than sniff message text, same as
 * the transfer/call error constants above.
 */
export const REGISTRATION_REJECTED = "The phone server rejected this seat's sign-in";

function describeError(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

/**
 * Turn the SIP status code that killed an outbound call into something an
 * agent can act on.
 *
 * These arrive on the INVITE's own request delegate, which is the ONLY place
 * they appear: SimpleUser's `onCallHangup` fires for a refused call exactly as
 * it does for one that ended normally, and carries no reason. Before this, a
 * declined call was reported to the agent as nothing whatsoever — the console
 * went quietly back to Ready, and a carrier refusing the number was
 * indistinguishable from a broken phone.
 *
 * Returns null for the codes an agent must NOT be alarmed about: 487 is what
 * our own CANCEL produces when they hang up before the far end picks up.
 */
export function describeCallRejection(statusCode: number, reasonPhrase?: string): string | null {
  switch (statusCode) {
    case 487: // Request Terminated — the agent cancelled it themselves.
      return null;
    case 486: // Busy Here
    case 600: // Busy Everywhere
      return "That line is busy";
    case 603: // Decline — a carrier or handset actively refusing us.
      return "They declined the call";
    case 404: // Not Found
    case 604: // Does Not Exist Anywhere
      return "That number isn't in service";
    case 480: // Temporarily Unavailable
      return "No answer — the line didn't pick up";
    case 403: // Forbidden
      return "This seat isn't allowed to call that number";
    case 402: // Payment Required
      return "Not enough credit to place that call";
    case 408: // Request Timeout
      return "The call timed out before anyone answered";
    case 503: // Service Unavailable — the usual shape of a carrier refusal.
    case 500:
      return "The carrier couldn't complete that call";
    default:
      return reasonPhrase
        ? `Call failed: ${reasonPhrase} (${statusCode})`
        : `Call failed (${statusCode})`;
  }
}

/**
 * Reduce an operator-typed target to something dialable.
 *
 * Agents type numbers the way they read them off a screen — "(585) 555-0142",
 * "555-0142", "+1 585 555 0142" — so punctuation and spaces are stripped
 * rather than rejected. `+`, `*` and `#` survive because they're meaningful in
 * a dial string (E.164 and feature codes).
 */
function normalizeTarget(raw: string): string {
  return raw.trim().replace(/[^0-9+*#]/g, "");
}

/**
 * Pull the host out of a registered AOR ("sip:1001@webrtc.reliadata.io" ->
 * "webrtc.reliadata.io"), dropping any URI parameters (";transport=ws") so the
 * host can be recombined with a different user part.
 */
function parseDomain(aor: string): string | null {
  const host = aor.split("@")[1];
  if (!host) return null;
  return host.split(/[;?]/)[0] || null;
}

/**
 * Spend a user gesture NOW to buy call audio later.
 *
 * Call this from the click that brings a console online — while the tap is
 * still the thing on the stack, and BEFORE the login/credential/REGISTER
 * round-trips, which take long enough to outlive the browser's transient
 * activation window.
 *
 * Mobile browsers grade autoplay per element: one gesture-backed play() marks
 * this element user-activated, so the un-gestured play() SIP.js issues when a
 * call is answered minutes later is allowed. Without it, the first call of a
 * shift is the one at risk — exactly when an agent is least likely to read
 * silence as a device problem rather than a dead line.
 *
 * Best-effort by design: an element with no source rejects, and browsers that
 * need no unlocking reject nothing. Neither is actionable, so both are
 * swallowed — unlike a failure once a call is up, which means a live call is
 * silent and is reported through `onAudioBlocked`.
 *
 * Standalone rather than a method because it must run before `connect()` has
 * handed the element to the Softphone instance.
 */
export async function primeAudioPlayback(audio: HTMLAudioElement | null): Promise<void> {
  if (!audio) return;
  audio.muted = false;
  if (audio.volume === 0) audio.volume = 1;
  try {
    await audio.play();
  } catch {
    /* nothing to play yet, or no unlock needed — the call path re-asserts */
  }
}

/** SimpleUser's live Session, if any — see `Softphone.currentSession`. */
function sessionOf(user: SimpleUser | null): Session | undefined {
  return (user as unknown as { session?: Session } | null)?.session;
}

/**
 * The SIP status a second caller hears while this seat is on a call. 486 is
 * the truthful one (busy, not unreachable), and it is what a queue treats as
 * "try again later" rather than "this member is gone".
 */
export const BUSY_HERE = 486;

/**
 * Refuse a second incoming call while one is live — 486 Busy Here, before
 * SimpleUser ever sees the INVITE.
 *
 * SimpleUser is documented as single-session and this class leaned on that.
 * For INBOUND calls it is not true: SessionManager's guard is
 * `managedSessions.length > maxSimultaneousSessions`, and SimpleUser sets the
 * maximum to 1, so exactly one live session lets a second INVITE straight
 * through (sip.js 0.21.2, session-manager.js `onInvite`). On the floor
 * (2026-09-18) that second call rang the console mid-transfer: `onCallReceived`
 * fired, the state went to "ringing", the call bar swapped to Answer/Decline
 * and the transfer controls vanished; and because SimpleUser had also moved its
 * own `session` pointer to the new Invitation, declining it (or the caller
 * giving up) fired `onCallHangup` -> "idle" for a call that was still up.
 *
 * The UserAgent delegate is the layer BELOW SessionManager: refusing there is
 * the only place the INVITE can be turned away without any of that
 * bookkeeping happening first. Anything still in progress on this seat —
 * ringing, dialling, established, tearing down — counts as busy; only a
 * Terminated session (or none) lets a call through.
 *
 * Belt to the queue's braces: the realtime queue also skips a member whose
 * device is in use, but a stray INVITE from any other source, or a queue that
 * rang a hair before the answer propagated, must never take the live call's
 * controls off the screen.
 */
function refuseSecondCall(user: SimpleUser): void {
  const manager = (user as unknown as { sessionManager?: { userAgent?: UserAgent } })
    .sessionManager;
  const delegate = manager?.userAgent?.delegate;
  const inner = delegate?.onInvite;
  if (!delegate || !inner) return;
  delegate.onInvite = (invitation: Invitation) => {
    const live = sessionOf(user);
    if (live && live.state !== SessionState.Terminated) {
      invitation.reject({ statusCode: BUSY_HERE }).catch(() => {
        // Best-effort: a reject that fails leaves the INVITE to time out on
        // Asterisk's side. It still never reaches the console.
      });
      return;
    }
    inner.call(delegate, invitation);
  };
}

/**
 * Thin wrapper around SIP.js's SimpleUser (0.21.x) that exposes the small
 * state machine + callback surface the agent panel UI (Task 4.2) needs,
 * without leaking SimpleUser's own richer API.
 */
export class Softphone {
  private user: SimpleUser | null = null;

  // Per-connect generation/epoch. Bumped at the start of every connect() and
  // every disconnect(); each connect() call captures the value it bumped to
  // (`myGeneration`) and re-checks it against `this.generation` at every
  // checkpoint after an `await`. If a disconnect() — or another connect() —
  // has run in the meantime, `this.generation` has moved on and the stale
  // call's remaining continuation (register(), delegate wiring, callbacks)
  // becomes a complete no-op instead of finishing into a component that has
  // already unmounted or moved on. This is what closes the residual
  // unmount-during-connect window: previously, a disconnect() that raced in
  // while connect() was still awaiting SIP.js's connect()/register() could
  // not stop that connect() call from eventually firing onState("idle") (or
  // an error callback) and wiring up a delegate on an instance the caller no
  // longer holds a reference to.
  private generation = 0;

  // Host part of the registered AOR (e.g. "webrtc.reliadata.io" out of
  // "sip:1001@webrtc.reliadata.io"), captured at connect() time. transfer()
  // needs it to turn a bare number an agent types into a routable SIP URI —
  // there is no other client-side source for the domain, and hardcoding one
  // would break the moment a node is renamed.
  private domain: string | null = null;

  // The live callback sink, captured at connect() time. Held on the instance
  // (not just closed over inside connect()) because call() reports outbound
  // progress and failures through the same channel, and it runs long after
  // connect() has returned. Cleared by teardown() alongside `user`, so a
  // late INVITE rejection landing after a disconnect goes nowhere.
  private cb: SoftphoneCallbacks | null = null;

  // The element SIP.js pipes remote audio into, captured at connect() time.
  // Held because it is the ONLY thing on the page that makes sound, and
  // nothing was ever checking it: the connectivity test's level meter taps the
  // MediaStream through WebAudio, which reads the decoded audio directly and
  // is completely independent of whether this element ever played. A phone
  // that emits nothing still drives that meter to full deflection.
  private audio: HTMLAudioElement | null = null;

  async connect(creds: SipCredentials, audio: HTMLAudioElement, cb: SoftphoneCallbacks): Promise<void> {
    // A retry-after-failure from the UI (the exact flow the connect()
    // catch-block's state -> "offline" enables) means connect() can be
    // called again while a previous SimpleUser is still around — tear it
    // down first so its WebSocket/RTCPeerConnection isn't orphaned and the
    // two instances can't race each other.
    const myGeneration = ++this.generation;
    await this.teardown();
    // Superseded by a disconnect() or another connect() that ran during the
    // teardown() await above — bail before ever creating a new SimpleUser.
    if (myGeneration !== this.generation) return;

    const options: SimpleUserOptions = {
      aor: creds.uri,
      media: { remote: { audio } },
      userAgentOptions: {
        authorizationUsername: creds.auth_user,
        authorizationPassword: creds.password,
        displayName: creds.display_name,
        // Wire the STUN/TURN servers issued by GET /api/agent/sip-credentials
        // (Task 3.1) into SIP.js's underlying RTCPeerConnection config, so
        // STUN actually applies to the call's ICE gathering.
        sessionDescriptionHandlerFactoryOptions: {
          peerConnectionConfiguration: {
            iceServers: creds.ice_servers,
          },
        },
      },
    };
    const user = new SimpleUser(creds.ws_server, options);
    refuseSecondCall(user);
    this.audio = audio;
    const isStale = () => myGeneration !== this.generation;
    user.delegate = {
      onCallReceived: async () => {
        if (isStale()) return;
        cb.onState("ringing");
        cb.onIncoming("caller");
      },
      onCallAnswered: () => {
        if (isStale()) return;
        cb.onState("on-call");
        // SIP.js has just assigned srcObject and called play() on our behalf,
        // and has already swallowed any rejection into its own logger. Assert
        // it again through a promise WE own, so a device that refuses to play
        // is reported instead of presenting as a normal call the agent simply
        // cannot hear.
        void this.ensurePlayback(() => isStale());
      },
      onCallHangup: () => {
        if (isStale()) return;
        cb.onState("idle");
      },
      onRegistered: () => {
        if (isStale()) return;
        cb.onState("idle");
      },
      onUnregistered: () => {
        if (isStale()) return;
        // Fires when Asterisk REJECTS a REGISTER (beads-209: e.g. an AOR
        // Asterisk cannot resolve for the request/To-URI user part) or a
        // previously-accepted registration lapses without a successful
        // refresh — either way the agent is no longer actually reachable,
        // so this is always a failure, never a silent "idle". A deliberate
        // unregister from this class's own teardown() can never land here:
        // teardown() detaches the delegate BEFORE ever calling
        // user.unregister().
        //
        // beads F2: a registration blip during a REAL, still-live call
        // (hasActiveSession() — the RTP media is independent of the
        // signaling transport this rejection came in on) must not collapse
        // the console's on-call UI out from under the agent — Hang Up/Mute/
        // Transfer stay reachable, and AgentConsole's own recovery path
        // (goOnline(), when hasActiveSession() is true) re-registers in
        // place instead of tearing the session down. The registration
        // problem is still surfaced via onError either way, so the agent
        // isn't kept in the dark, just without losing the call they're on.
        if (!this.hasActiveSession()) {
          cb.onState("offline");
        }
        cb.onError(REGISTRATION_REJECTED);
      },
      onServerDisconnect: (error) => {
        if (isStale()) return;
        // Fires whenever the transport drops, with or without an error —
        // normally the softphone is no longer connected, so state moves to
        // "offline". Same beads F2 exception as onUnregistered above: never
        // collapse the on-call UI while a real call is still live.
        if (!this.hasActiveSession()) {
          cb.onState("offline");
        }
        if (error) {
          cb.onError(`Lost connection to the phone server: ${describeError(error)}`);
        }
      },
    };
    // Nothing async has happened since the freshness check above, so this
    // generation is still current — safe to commit this instance as THE
    // softphone's user before any further awaits.
    this.user = user;
    this.domain = parseDomain(creds.uri);
    this.cb = cb;
    cb.onState("registering");
    try {
      await user.connect();
      // A disconnect() that raced in during the await above has already
      // torn `user` down via teardown() (unregister/disconnect + delegate
      // detached) by the time we get here — do not also call register() or
      // fire any callback into a caller that has moved on.
      if (isStale()) return;
      await user.register();
      // Deliberately no `cb.onState("idle")` here. SIP.js's register()
      // resolves once the REGISTER request is SENT, not once Asterisk
      // ACCEPTS it (beads-209: with a misconfigured AOR, Asterisk rejected
      // every REGISTER while this used to fire "idle" anyway — the console
      // showed "Ready" for an agent nobody could route a call to). The state
      // only becomes "idle" from the `onRegistered` delegate callback above,
      // fired once the Registerer's own state machine confirms a 200
      // (RegistererState -> Registered); a rejection instead fires
      // `onUnregistered`, handled above as a hard, reported failure.
    } catch (err) {
      if (isStale()) return;
      cb.onState("offline");
      cb.onError(`Could not connect the softphone: ${describeError(err)}`);
    }
  }

  async answer(): Promise<void> {
    await this.user?.answer();
  }

  async hangup(): Promise<void> {
    await this.user?.hangup();
  }

  /**
   * True if there is a real, live call session behind this instance right
   * now (ringing, negotiating, established, or mid-teardown) — as opposed
   * to a `PhoneState` value elsewhere that merely SAYS "ringing"/"on-call"
   * but no longer matches reality.
   *
   * Exists so a consumer holding a possibly-stale copy of `PhoneState`
   * (propagated up through React state, one render behind, or simply never
   * reconciled after an edge-case session teardown) can verify against the
   * source of truth before treating that value as busy. Confirmed live:
   * beads_php_master_phone_system-0uf found a seat whose console had shown
   * "Ready"/idle throughout, yet whose registration had actually been lost
   * — the mirror-image staleness (state says fine, reality isn't) of the
   * "state says busy, reality is idle" case this method exists to catch.
   */
  hasActiveSession(): boolean {
    const session = this.currentSession();
    return !!session && session.state !== SessionState.Terminated;
  }

  /**
   * Force a fresh REGISTER, re-verifying the registration rather than
   * trusting whatever SIP.js's own internal refresh timer last reported.
   *
   * SIP.js schedules its periodic re-REGISTER on an ordinary
   * setTimeout/setInterval — exactly what mobile browsers throttle or
   * outright pause for a backgrounded tab. A scheduled refresh can simply
   * never fire while the tab is hidden, silently letting the AOR contact
   * expire on Asterisk's side while this console still shows "Ready" — live
   * evidence (beads_php_master_phone_system-0uf): `pjsip show aor` reported
   * zero contacts for a seat whose console had never left "Ready".
   *
   * Intended to be called when the page becomes visible again (see
   * AgentConsole's `visibilitychange` handler) so a genuinely lost
   * registration surfaces — through the SAME `onUnregistered`/`onError`
   * delegate path as any other rejected REGISTER — within moments of the
   * agent actually looking at the screen again, rather than staying
   * silently wrong indefinitely. A successful refresh is a deliberate no-op
   * from the UI's point of view: nothing needs to change when reality
   * already matches what's on screen.
   *
   * Never throws — a failed re-REGISTER attempt is surfaced through that
   * same delegate path, not through this method's return.
   */
  async verifyRegistration(): Promise<void> {
    if (!this.user) return;
    try {
      await this.user.register();
    } catch {
      // Reported via onUnregistered/onError already firing off of this same
      // attempt — nothing further to do here.
    }
  }

  /**
   * Blind-transfer the live call to `target` via SIP REFER.
   *
   * `target` may be a bare number/extension ("18885550142", "1001") — which is
   * resolved against the registered domain — or a full "sip:..." URI for
   * transferring to another call center outright.
   *
   * This is a *blind* (unattended) transfer: the far end is handed the new
   * destination and the agent drops out of the call. There is deliberately no
   * consultation leg — SimpleUser only models one concurrent session, so an
   * attended transfer would need the full SessionManager API. The agent
   * therefore never hears whether the target picked up, and `await` returning
   * only means the REFER was accepted, not that the transfer completed. The
   * subsequent onCallHangup is what actually tells the UI the agent is out.
   *
   * Throws `TRANSFER_INVALID_TARGET` / `TRANSFER_NO_CALL` for the two
   * agent-actionable failures; any other rejection is a genuine REFER failure
   * for the caller to report generically.
   */
  async transfer(target: string): Promise<void> {
    const session = this.currentSession();
    // REFER is only legal inside an established dialog. A call that is still
    // ringing (or has just ended under the agent) has no dialog to refer, and
    // sending one anyway makes SIP.js throw an opaque state error.
    if (!session || session.state !== SessionState.Established) {
      throw new Error(TRANSFER_NO_CALL);
    }

    const uri = this.resolveDialUri(target);
    if (!uri) throw new Error(TRANSFER_INVALID_TARGET);

    await session.refer(uri);
  }

  /**
   * Place an outbound call to `target` — a bare number/extension (resolved
   * against the registered domain, same rule as transfer()) or a full
   * "sip:..." URI.
   *
   * Used by the agent console's key pad and by the connectivity self-test's
   * echo/loopback call (`services/connectionTest.ts`). Reuses the exact same
   * domain-resolution rule as transfer() so a dialled call, a self-test call
   * and a transfer never disagree about what a bare number means.
   *
   * Where the call lands is entirely the PBX's business: the INVITE arrives in
   * `ictbroadcast-agent-outbound`, which resolves the seat from the
   * authenticated endpoint and decides caller ID, trunk, and whether the
   * account may dial at all. Nothing this method sends is trusted for any of
   * that.
   */
  async call(target: string): Promise<void> {
    if (!this.user) throw new Error(CALL_NOT_CONNECTED);
    const uri = this.resolveDialUri(target);
    if (!uri) throw new Error(CALL_INVALID_TARGET);
    const cb = this.cb;
    const myGeneration = this.generation;
    const isStale = () => myGeneration !== this.generation;

    // Announce the attempt BEFORE awaiting. `call()` below resolves once the
    // INVITE has been SENT — not once anyone answers — so the promise is
    // useless as a "call in progress" signal. Everything the agent sees while
    // the far end rings hangs off this state instead.
    cb?.onState("calling");
    try {
      await this.user.call(
        uri.toString(),
        {
          // Play the carrier's early media (the 183 Session Progress stream).
          // This IS the ringback an agent hears: our dialplan's Dial() has no
          // `r` flag and this client generates no local tone, so the ring is
          // whatever the carrier sends — and SIP.js discards it unless this
          // flag is set (session-manager.js only calls setupRemoteMedia() on
          // a 183 when `earlyMedia` is on). Without it the agent got dead
          // silence from pressing Call until the moment of answer.
          earlyMedia: true,
        },
        {
          requestDelegate: {
            onReject: (response) => {
              if (isStale()) return;
              const message = describeCallRejection(
                response.message.statusCode ?? 0,
                response.message.reasonPhrase,
              );
              // A refused call leaves no other trace: SimpleUser's
              // onCallHangup reports it exactly like a normal end.
              if (message) cb?.onCallFailed?.(message);
              cb?.onState("idle");
            },
          },
        },
      );
    } catch (err) {
      // The INVITE could not even be sent — put the console back where it was
      // before blaming the network, then let the caller report it.
      if (!isStale()) cb?.onState("idle");
      throw err;
    }
  }

  /**
   * Send a DTMF tone on the established call — what the key pad does once a
   * call is up, so an agent can work an IVR ("press 2 for billing") or enter
   * an extension after the auto-attendant answers.
   *
   * Silently does nothing when there is no established call. A tone with no
   * call is not an error worth interrupting an agent over; it just has
   * nowhere to go. Anything not a single 0-9/*​/# character is rejected
   * outright rather than passed to SIP.js, which would throw on it.
   */
  sendDtmf(tone: string): void {
    if (!/^[0-9*#]$/.test(tone)) return;
    const session = this.currentSession();
    if (!session || session.state !== SessionState.Established) return;
    // RFC 2833 out-of-band tones via INFO — SimpleUser's own path, and the one
    // Asterisk's WebRTC endpoints negotiate.
    void this.user?.sendDTMF(tone).catch((err) => {
      // A rejected tone is not actionable by the agent — they can press again.
      console.warn("softphone.sendDtmf failed", describeError(err));
    });
  }

  /**
   * Resolve a bare number/extension or a full "sip:..." URI into a dialable
   * URI, using the domain captured from the registered AOR at connect() time.
   * Shared by transfer() (REFER) and call() (INVITE) so the two never
   * disagree about what a bare number means. Returns undefined for anything
   * with no dialable characters or an unparseable full URI.
   */
  private resolveDialUri(target: string) {
    const digits = normalizeTarget(target);
    const raw = target.trim();
    const uriText = raw.toLowerCase().startsWith("sip:")
      ? raw
      : digits && this.domain
        ? `sip:${digits}@${this.domain}`
        : "";
    // makeURI returns undefined rather than throwing on unparseable input, so
    // this catches both an empty/garbage number and a malformed pasted URI.
    return uriText ? UserAgent.makeURI(uriText) : undefined;
  }

  /**
   * The remote party's live media stream, if a call is connected. Undefined
   * before answer/connect and after hangup. Used by the connectivity
   * self-test to drive a level meter on the echoed audio, proving the
   * receive direction actually carries sound rather than just trusting the
   * session state.
   */
  remoteStream(): MediaStream | undefined {
    return this.user?.remoteMediaStream;
  }

  /**
   * Is the receive path silent on THIS device despite a healthy call?
   *
   * True when the element that plays remote audio is paused, muted, or turned
   * all the way down. Deliberately separate from anything that inspects the
   * MediaStream: a stream can be arriving and decoding perfectly — the level
   * meter bouncing, `getStats()` counting packets — while this returns true,
   * and in that state the agent hears nothing at all.
   */
  playbackBlocked(): boolean {
    const audio = this.audio;
    if (!audio || !audio.srcObject) return false;
    return audio.paused || audio.muted || audio.volume === 0;
  }

  /**
   * Ask the element to play, from a real user gesture.
   *
   * Mobile browsers reject `play()` outside a gesture, so this is the one
   * recovery that can work once `onAudioBlocked` has fired — it must be called
   * from a tap handler, not from a timer or an effect. Resolves to whether the
   * element is playing afterwards.
   */
  async resumePlayback(): Promise<boolean> {
    const audio = this.audio;
    if (!audio) return false;
    // A blocked element is often also left muted by the browser's own
    // fallback ("play muted instead of not at all"), so clear that too —
    // otherwise play() resolves and the agent still hears silence.
    audio.muted = false;
    if (audio.volume === 0) audio.volume = 1;
    try {
      await audio.play();
    } catch {
      return false;
    }
    return !audio.paused;
  }

  /**
   * Confirm the answered call is actually audible here, and say so if not.
   *
   * SIP.js's own play() has already run and already discarded its rejection by
   * the time this is reached, so the result is read back off the element
   * rather than inferred from that call. `paused` is the load-bearing check:
   * after a rejected play() the element stays paused with a live srcObject,
   * which is precisely the silent-but-connected state being caught.
   */
  private async ensurePlayback(isStale: () => boolean): Promise<void> {
    const audio = this.audio;
    if (!audio) return;
    try {
      await audio.play();
    } catch {
      /* fall through to the paused check — the state is what matters */
    }
    if (isStale() || !this.audio) return;
    if (audio.paused || audio.muted || audio.volume === 0) {
      this.cb?.onAudioBlocked?.();
    }
  }

  /**
   * The live Session behind SimpleUser, if any.
   *
   * SimpleUser declares `session` private and exposes no accessor, so there is
   * no typed way to reach the Session that REFER needs — but the field is a
   * plain runtime property of the unbundled `sip.js/lib` build we import, kept
   * in sync by SimpleUser's own delegate. This one narrow cast is the whole
   * cost of not having to reimplement SimpleUser on top of SessionManager.
   */
  private currentSession(): Session | undefined {
    return sessionOf(this.user);
  }

  async disconnect(): Promise<void> {
    // Claim a new generation FIRST — before the teardown() await — so any
    // connect() call still in flight (its own await, anywhere in its
    // continuation) observes a generation mismatch the next time it checks
    // and makes its remaining work a no-op, even though this disconnect()
    // hasn't finished tearing down yet.
    this.generation++;
    await this.teardown();
  }

  mute(on: boolean): void {
    on ? this.user?.mute() : this.user?.unmute();
  }

  /**
   * Put the current call on hold, or take it back off.
   *
   * Deliberately NOT the same kind of operation as mute(), despite sitting
   * next to it in the UI. mute() flips a local track's `enabled` flag: it
   * cannot fail, it is invisible to the far end, and the customer hears dead
   * air. hold() sends a re-INVITE whose offer is `sendonly`, which is what
   * makes ASTERISK start playing its music-on-hold class at the customer
   * (`moh_suggest`, `default` on this deployment) instead of silence. That is
   * the whole reason hold exists separately from mute here.
   *
   * Being a real SIP transaction, it can fail — the far end can reject the
   * re-INVITE, and sip.js rejects outright if no session exists or a hold
   * request is already in flight. Errors propagate rather than being
   * swallowed so the console can resync its button from isHeld() instead of
   * showing "On hold" over a call that is still very much live. sip.js
   * already undoes its own optimistic state on failure and no-ops when the
   * session is in the requested state, so this wrapper stays thin.
   */
  async hold(on: boolean): Promise<void> {
    const user = this.user;
    // Same gate as transfer(), and for the same reason: a re-INVITE is only
    // legal inside an established dialog. hasActiveSession() is deliberately
    // looser — it counts a call that is merely ringing — so holding on it
    // would let an agent "hold" a call that has no dialog to renegotiate,
    // and SIP.js would throw an opaque state error instead of the plain
    // sentence the console shows.
    const session = user ? this.currentSession() : null;
    if (!user || !session || session.state !== SessionState.Established) {
      throw new Error(HOLD_NO_CALL);
    }
    on ? await user.hold() : await user.unhold();
  }

  /**
   * Whether the current call is on hold. Safe with no session (returns
   * false), so the console can call it unconditionally after any hold
   * attempt — including a failed one — to get the truth rather than assuming
   * its own optimistic toggle took.
   */
  isHeld(): boolean {
    return this.user?.isHeld() ?? false;
  }

  /**
   * Best-effort teardown of the current SimpleUser, if any.
   *
   * beads F1/F2 — the whole point of this change: if a real call is up
   * (hasActiveSession(), checked BEFORE anything below runs), send a hangup
   * first. SimpleUser.hangup() -> SessionManager.terminate() sends a real
   * BYE for an Established session (CANCEL/reject for one still ringing) —
   * verified directly against the installed sip.js source
   * (node_modules/sip.js/lib/platform/web/session-manager/session-manager.js,
   * `terminate()`'s `SessionState.Established` branch calls `session.bye()`).
   * Without this, every path into teardown() — an explicit disconnect(), a
   * session-expiry unmount, or connect()'s own unconditional teardown-first
   * — went straight to UserAgent.stop()'s non-graceful branch (no
   * `gracefulShutdown` option is set below), which disposes the transport
   * and resets `_sessions = {}` with NO BYE ever sent (also verified
   * directly against user-agent.js's `stop()`). A live customer call was
   * being silently cut off instead of properly ended.
   *
   * Detaches the delegate *before* unregistering/disconnecting so a late or
   * async server event (e.g. onServerDisconnect firing off the disconnect()
   * call itself) can never reach into a component that has already moved on
   * — and clears `this.user` up front so answer()/hangup()/mute() called
   * during this teardown's own await gap are immediately inert rather than
   * racing against a half-torn-down instance.
   *
   * Errors from the old instance's hangup()/unregister()/disconnect() are
   * swallowed: this is cleanup of something we're discarding either way, and
   * a flaky previous instance must never block connect() from building a
   * fresh one or disconnect() from completing.
   */
  private async teardown(): Promise<void> {
    const user = this.user;
    if (!user) return;
    const hadActiveSession = this.hasActiveSession();
    this.user = null;
    this.domain = null;
    this.cb = null;
    this.audio = null;
    user.delegate = undefined;
    if (hadActiveSession) {
      try {
        await user.hangup();
      } catch {
        // ignore — best-effort; the point is the BYE/CANCEL attempt itself
      }
    }
    try {
      await user.unregister();
    } catch {
      // ignore — best-effort cleanup of a discarded instance
    }
    try {
      await user.disconnect();
    } catch {
      // ignore — best-effort cleanup of a discarded instance
    }
  }
}
